import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import queryString from "query-string";
import axios from "axios";

import api from "libs/api";
import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";

import { useApp } from "context/app";

const TasksContext = createContext();

const TasksProvider = ({ children }) => {
  const { websites } = useApp();
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [init_loading, setInitLoading] = useState(true);
  const [file_progress, setFileProgress] = useState(null);

  const { page: _page, ...filters } = queryString.parse(search);
  const default_sort = "created_at:desc";
  const ignoreFilters = pathname === "/";
  const stringifyFilters = queryString.stringify({
    status:
      task_status_data
        ?.filter((item) => filters?.status?.split(",").includes(item?.key))
        .map((item) => item?.value)
        .join(",") || undefined,
    priority:
      task_priority_data
        ?.filter((item) => filters?.priority?.split(",").includes(item?.key))
        .map((item) => item?.value)
        .join(",") || undefined,
    sort: filters?.sort || default_sort,
  });

  const {
    isLoading: loading,
    data,
    refetch: refetchTasks,
    isSuccess,
  } = useQuery({
    queryKey: ["tasks", ignoreFilters ? "all" : filters],
    queryFn: () =>
      api.get(`/admin/tasks${ignoreFilters ? "" : `?${stringifyFilters}`}`),
  });

  useEffect(() => {
    if (isSuccess) {
      setInitLoading(false);
    }
  }, [isSuccess]);

  const { data: webmasters } = useQuery({
    queryKey: ["webmasters"],
    queryFn: () => api.get("/admin/webmasters?sort=first_name:asc"),
  });

  const createTask = (values) =>
    new Promise(async (resolve, reject) => {
      try {
        const task = await api.post(`/admin/tasks`, values);
        await createFirstActivity(task._id, values.finished_when, values.files);

        if (task) {
          navigate(`/tasks/${task._id}`);
        }

        resolve();
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong.");
        reject(err);
      }
    });

  const createFirstActivity = async (id, text, files) => {
    setFileProgress(0);

    const formData = new FormData();
    formData.append("text", text);
    files?.forEach((item) => {
      formData.append("files", item);
    });

    try {
      await axios.post(
        `${process.env.REACT_APP_API}/admin/tasks/${id}/send-message`,
        formData,
        {
          withCredentials: true,
          onUploadProgress: ({ loaded, total }) => {
            const percent = Math.floor((loaded * 100) / total);
            setFileProgress(percent);
          },
        },
      );
    } catch (err) {
      console.error(err);
    }

    setFileProgress(null);
  };

  const updateTask = (id, values) =>
    new Promise(async (resolve, reject) => {
      const taskQueries = queryClient.getQueriesData({
        queryKey: ["tasks"],
        predicate: (query) => query.queryKey[0] === "tasks",
      });

      const previousQueryStates = new Map();

      try {
        taskQueries.forEach(([queryKey, queryData]) => {
          if (!queryData?.tasks) return;

          previousQueryStates.set(queryKey, { ...queryData });

          queryClient.setQueryData(queryKey, (oldData) => {
            if (!oldData?.tasks) return oldData;

            const queryFilters = queryKey[1] || {};

            return {
              ...oldData,
              tasks: oldData.tasks
                .map((task) => {
                  if (task._id === id) {
                    const updatedTask = { ...task, ...values };

                    if (queryFilters.status) {
                      const statusValue = task_status_data?.find(
                        (item) => item?.key === queryFilters.status,
                      )?.value;

                      if (statusValue && updatedTask.status !== statusValue) {
                        return null;
                      }
                    }

                    if (queryFilters.type) {
                      const typeValue = task_priority_data?.find(
                        (item) => item?.key === queryFilters.type,
                      )?.value;

                      if (typeValue && updatedTask.priority !== typeValue) {
                        return null;
                      }
                    }

                    return updatedTask;
                  }

                  return task;
                })
                .filter(Boolean),
            };
          });
        });

        await api.put(`/admin/tasks/${id}`, values);

        await queryClient.invalidateQueries({
          queryKey: ["tasks"],
        });

        resolve();
      } catch (err) {
        taskQueries.forEach(([queryKey]) => {
          const originalData = previousQueryStates.get(queryKey);
          if (originalData) {
            queryClient.setQueryData(queryKey, originalData);
          }
        });

        console.error(err);
        toast.error("Something went wrong.");
        reject(err);
      }
    });

  const filterClearAll = () => {
    const query = filters?.sort
      ? `?${queryString.stringify({ sort: filters.sort })}`
      : "";

    navigate(`/tasks${query}`);
  };

  const applyFilters = (newFilters) => {
    const merged = { ...filters };

    Object.entries(newFilters).forEach(([name, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === false ||
        value === ""
      ) {
        delete merged[name];
      } else {
        merged[name] = typeof value === "boolean" ? value : value?.key;
      }
    });

    navigate(`/tasks?${queryString.stringify(merged)}`);
  };

  const filterHandler = (name, value, accumulate = false) => {
    let accumulated_value = value;

    if (accumulate) {
      const currentValues = filters[name] ? filters[name].split(",") : [];

      if (value) {
        if (currentValues.includes(value)) {
          const filteredValues = currentValues.filter((val) => val !== value);
          accumulated_value =
            filteredValues.length > 0 ? filteredValues.join(",") : undefined;
        } else {
          currentValues.push(value);
          accumulated_value = currentValues.join(",");
        }
      } else {
        accumulated_value =
          currentValues.length > 0 ? currentValues.join(",") : undefined;
      }
    }

    navigate(
      `/tasks?${queryString.stringify({
        ...filters,
        [name]: accumulated_value || undefined,
      })}`,
    );
  };

  const value = useMemo(() => {
    return {
      data: data?.tasks,
      websites: data?.websites,
      webmasters,
      loading,
      init_loading,
      file_progress,
      filters,
      filterHandler,
      filterClearAll,
      applyFilters,
      createTask,
      updateTask,
      refetchTasks,
    };
    // eslint-disable-next-line
  }, [
    data,
    websites,
    webmasters,
    loading,
    init_loading,
    file_progress,
    filters,
    applyFilters,
  ]);

  return (
    <TasksContext.Provider value={value}>{children}</TasksContext.Provider>
  );
};

const useTasks = () => useContext(TasksContext);
export { TasksContext, useTasks };
export default TasksProvider;
